import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { KeyboardEvent } from 'react';
import { textDirection } from '../utils/stringComboboxUtils';
import { reportAppError } from '../utils/errorReport';

export interface StringComboboxConfig {
  removedStorageKey: string;
  fetchItems: (search?: string, limit?: number) => Promise<{ success: boolean; data?: unknown }>;
  parseItems: (response: { success: boolean; data?: unknown }) => string[];
  logLabel: string;
}

const getRemovedItems = (storageKey: string): string[] => {
  try {
    const stored = localStorage.getItem(storageKey);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

export const useStringCombobox = (
  value: string,
  onChange: (value: string) => void,
  config: StringComboboxConfig
) => {
  const { removedStorageKey, fetchItems, parseItems, logLabel } = config;

  const [existingItems, setExistingItems] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoadedInitial, setHasLoadedInitial] = useState(false);
  const [fieldTitle, setFieldTitle] = useState<string | undefined>(undefined);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const inputContainerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const optionRefs = useRef<(HTMLButtonElement | null)[]>([]);

  useLayoutEffect(() => {
    const el = nameInputRef.current;
    const name = (value || '').trim();
    if (!el) return;
    if (name && el.scrollWidth > el.clientWidth + 1) {
      setFieldTitle(name);
    } else {
      setFieldTitle(undefined);
    }
    if (document.activeElement !== el && textDirection(name) === 'ltr') {
      el.scrollLeft = 0;
    }
  }, [value, showSuggestions]);

  const loadItems = async (searchTerm: string = '', limit = 50) => {
    try {
      if (!hasLoadedInitial) {
        setIsLoading(true);
      }
      const response = await fetchItems(searchTerm || undefined, limit);
      if (!response.success) {
        reportAppError(`שגיאה בטעינת ${logLabel}`, {
          action: `Load ${logLabel} list`,
          error: 'Request returned success: false',
        });
        return [];
      }
      const items = parseItems(response);
      const removed = getRemovedItems(removedStorageKey);
      const filtered = items.filter((item) => !removed.includes(item));
      setExistingItems(filtered);
      setHasLoadedInitial(true);
      return filtered;
    } catch (error) {
      reportAppError(`שגיאה בטעינת ${logLabel}`, {
        action: `Load ${logLabel} list`,
        error,
      });
      return [];
    } finally {
      setIsLoading(false);
    }
  };

  const openSuggestions = async (options?: { resetSearch?: boolean }) => {
    const resetSearch = options?.resetSearch !== false;
    if (resetSearch) {
      setSearchQuery('');
    }
    setHighlightedIndex(-1);
    setShowSuggestions(true);
    await loadItems(resetSearch ? '' : searchQuery.trim(), 50);
    requestAnimationFrame(() => nameInputRef.current?.focus());
  };

  const handleNameChange = (text: string) => {
    onChange(text);
    setSearchQuery(text);
    if (!showSuggestions) {
      setShowSuggestions(true);
      setHighlightedIndex(-1);
      void loadItems(text.trim().length >= 2 ? text.trim() : '', 50);
    }
  };

  const closeSuggestions = () => {
    setShowSuggestions(false);
    setSearchQuery('');
    setHighlightedIndex(-1);
  };

  const selectItem = (item: string) => {
    onChange(item);
    closeSuggestions();
  };

  const filteredItems = useMemo(() => {
    const removed = getRemovedItems(removedStorageKey);
    const q = searchQuery.trim().toLowerCase();
    return existingItems
      .filter((item) => !removed.includes(item))
      .filter((item) => !q || item.toLowerCase().includes(q))
      .slice(0, 50);
  }, [existingItems, searchQuery, removedStorageKey]);

  const removeItem = (itemToRemove: string) => {
    const removed = getRemovedItems(removedStorageKey);
    if (!removed.includes(itemToRemove)) {
      removed.push(itemToRemove);
      localStorage.setItem(removedStorageKey, JSON.stringify(removed));
      setExistingItems((prev) => prev.filter((item) => item !== itemToRemove));
    }
  };

  const focusNextField = useCallback(() => {
    requestAnimationFrame(() => {
      const root =
        inputContainerRef.current?.closest('form') ||
        inputContainerRef.current?.closest('[class*="formSection"]') ||
        document;
      const focusables = Array.from(
        root.querySelectorAll<HTMLElement>(
          'input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), button:not([disabled])'
        )
      ).filter((el) => el.offsetParent !== null);
      const idx = focusables.indexOf(nameInputRef.current!);
      if (idx >= 0 && idx < focusables.length - 1) {
        focusables[idx + 1]?.focus();
      }
    });
  }, []);

  const handleSearchKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        closeSuggestions();
        nameInputRef.current?.focus();
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        if (filteredItems.length === 0) return;
        setHighlightedIndex((i) => (i < 0 ? 0 : Math.min(i + 1, filteredItems.length - 1)));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        if (filteredItems.length === 0) return;
        setHighlightedIndex((i) => (i <= 0 ? 0 : i - 1));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < filteredItems.length) {
          selectItem(filteredItems[highlightedIndex]);
        } else if (filteredItems.length === 1) {
          selectItem(filteredItems[0]);
        } else if ((searchQuery || value).trim()) {
          selectItem((searchQuery || value).trim());
        }
      } else if (e.key === 'Tab' && !e.shiftKey) {
        if (highlightedIndex >= 0 && highlightedIndex < filteredItems.length) {
          e.preventDefault();
          selectItem(filteredItems[highlightedIndex]);
          focusNextField();
        }
      }
    },
    [filteredItems, highlightedIndex, searchQuery, value, focusNextField]
  );

  useEffect(() => {
    loadItems('', 50);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!showSuggestions) return undefined;
    const q = searchQuery.trim();
    if (q.length === 0) {
      loadItems('', 50);
      return undefined;
    }
    if (q.length < 2) return undefined;
    const timeoutId = setTimeout(() => {
      loadItems(q, 50);
    }, 250);
    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, showSuggestions]);

  useEffect(() => {
    if (!showSuggestions) return undefined;
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (inputContainerRef.current?.contains(target)) return;
      closeSuggestions();
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [showSuggestions]);

  useEffect(() => {
    setHighlightedIndex(-1);
  }, [searchQuery]);

  useEffect(() => {
    setHighlightedIndex((i) => {
      if (filteredItems.length === 0) return -1;
      if (i >= filteredItems.length) return filteredItems.length - 1;
      return i;
    });
  }, [filteredItems]);

  useEffect(() => {
    if (highlightedIndex >= 0) {
      optionRefs.current[highlightedIndex]?.scrollIntoView({ block: 'nearest' });
    }
  }, [highlightedIndex]);

  return {
    inputContainerRef,
    searchInputRef,
    nameInputRef,
    optionRefs,
    fieldTitle,
    showSuggestions,
    searchQuery,
    setSearchQuery,
    isLoading,
    filteredItems,
    highlightedIndex,
    setHighlightedIndex,
    openSuggestions,
    closeSuggestions,
    selectItem,
    removeItem,
    handleSearchKeyDown,
    handleNameChange,
  };
};
